import { Translate } from 'next-translate';
import { DEFAULT_CUSTOM_COLUMN_ITEM } from '@components/bid_request/details/constants';
import { CustomColumnHeader, Lot } from 'types';

/**
 * Checks for duplicates in the custom column headers for a lot.
 * @param headers {CustomColumnHeader[]} List of custom column headers
 * @param lotName {String} Name of the lot for error messaging
 * @param t {Translate}
 * @returns {string[]|[]} List of errors or empty array if none are found.
 */
const duplicateHeadersCheck = (
  headers: CustomColumnHeader[],
  lotName: string,
  t: Translate
): string[] | [] => {
  const headersMap = headers.reduce<{ [key: string]: number }>(
    (acc, header) => {
      // Skip null/undefined/blank values - they'll be caught by blank validation
      const displayText = header.display_text?.trim();
      if (displayText) {
        acc[displayText] = (acc[displayText] || 0) + 1;
      }
      return acc;
    },
    {}
  );

  return Object.keys(headersMap)
    .filter(key => headersMap[key] > 1)
    .map(key =>
      t('lots.errors.duplicate_header', { header: key, lot_name: lotName }));
};

/**
 * Validates a custom column header
 * @param t {Translate}
 * @param header {CustomColumnHeader}
 * @param lotName {string}
 * @returns {string[]|[]} List of errors or empty array if none are found.
 */
const customColumnHeaderValidation = (
  t: Translate,
  header: CustomColumnHeader,
  lotName: string
): string[] | [] => {
  const errors: string[] = [];
  const displayText = header.display_text?.trim() || '';

  if (
    header.column_type === DEFAULT_CUSTOM_COLUMN_ITEM().column_type
    && displayText === ''
  ) {
    errors.push(t('lots.errors.save.headers.blank', { lot_name: lotName }));
  }

  return errors;
};

/**
 * Validates a lots custom column headers does not contain duplicates or blank headers
 * @param t
 * @param lot
 * @returns {string[]|[]} List of errors or empty array if none are found.
 */
export const lotValidation = (t: Translate, lot: Lot): string[] | [] => {
  const {
    errors = [],
    custom_column_headers: customColumnHeaders = [],
    name = '',
  } = lot;

  // Header Rules
  errors.push(...duplicateHeadersCheck(customColumnHeaders, name, t));

  // Individual Headers validation
  errors.push(
    ...customColumnHeaders.flatMap(header =>
      customColumnHeaderValidation(t, header, name).map(error => error))
  );

  return errors;
};
